import type { MemberOut, TeamOut } from "@sagot/api-client";
import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Icon } from "@sagot/ui";
import { useAgentList } from "../features/workspace/shared/use-agent-list";
import { useAgentConfig } from "../features/build/shared/queries";
import { useWorkspaceContext } from "../session/workspace-context";
import { describeRouting, type AgentRouting } from "./routing-model";
import { routingRules } from "./team-model";

const MAX_AGENTS = 3;

type AgentItem = NonNullable<ReturnType<typeof useAgentList>["data"]>[number];

function targetText(routing: AgentRouting, t: ReturnType<typeof useTranslation>["t"]): string {
  const { target } = routing;
  if (target.kind === "team") return t("team.teams.routing.toTeam", { name: target.name });
  if (target.kind === "person") return t("team.teams.routing.toPerson", { name: target.name });
  return t("team.teams.routing.toFallback");
}

function AgentRoutingLine({ agent, teams, members, showName }: { agent: AgentItem; teams: readonly TeamOut[]; members: readonly MemberOut[]; showName: boolean }) {
  const { t } = useTranslation();
  const config = useAgentConfig(agent.id);
  if (!config.data) return null;
  const routing = describeRouting(config.data.escalation, teams, members);
  return (
    <li data-testid="agent-routing">
      {showName ? <strong className="text-fg">{agent.name}: </strong> : null}
      {targetText(routing, t)}
      {routing.payment ? ` ${t("team.teams.routing.payment", { name: routing.payment })}` : null}
      {routing.matchTopics ? null : ` ${t("team.teams.routing.topicsOff")}`}
    </li>
  );
}

/**
 * Where handoffs really go: each agent's chosen target (Agent Settings > Escalation & handoff is
 * the one place it is stored), then the topic rules that win over it and what happens to teams
 * without members.
 */
export function RoutingNote({ teams, members }: { teams: readonly TeamOut[]; members: readonly MemberOut[] }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const agents = useAgentList();
  const live = (agents.data ?? []).slice(0, MAX_AGENTS);
  const rules = routingRules(teams);
  return (
    <div className="border-t border-line p-5">
      <div className="flex items-start gap-2.5 rounded-md bg-surface-2 p-3 text-xs text-fg-muted">
        <Icon name="headset" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
        <div className="min-w-0 space-y-1.5">
          <strong className="text-fg">{t("team.teams.routingTitle")}</strong>
          {live.length > 0 ? (
            <ul className="space-y-1">
              {live.map((agent) => (
                <AgentRoutingLine key={agent.id} agent={agent} teams={teams} members={members} showName={live.length > 1} />
              ))}
            </ul>
          ) : null}
          <p>
            {rules.length > 0
              ? t("team.teams.routing.topicRules", { rules: rules.map((r) => t("team.teams.routingRule", { topics: r.topics, team: r.team })).join("; ") })
              : t("team.teams.routing.noTopics")}
          </p>
          <p>{t("team.teams.routing.queue")}</p>
        </div>
      </div>
      {live[0] ? (
        <AppLink href={consoleRoutes.agentSettings(summary.slug, live[0].id, "escalation")} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-ink hover:underline">
          {t("team.teams.editRouting")}
          <Icon name="arrow-right" className="size-3.5" />
        </AppLink>
      ) : null}
    </div>
  );
}
